import { Classroom, ScheduleEvent, Session } from "@models/ISchema";
import { getSelectableDay } from "./format";
import { IntervalWeek } from "../context/types";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { getDateNumber } from "@utils/formats";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const getParsedDatetime = (event: ScheduleEvent, index?: number) => {
  const [date, rawStartTime] = (event?.start || "").split(" ");
  const [, rawEndTime] = (event?.end || "").split(" ");

  const [startHour, startMins] = rawStartTime.split(":");
  const startTime = `${startHour}:${startMins}`;

  const [endHour, endMins] = rawEndTime.split(":");
  const endTime = `${endHour}:${endMins}`;

  return {
    day: { value: date, error: false, index },
    start: { value: { label: startTime, value: startTime }, error: false, index },
    end: {
      value: { label: endTime, value: endTime },
      error: false,
      recurrence: false,
      index,
    },
    index,
  };
};

export const getUniqueRecurrentSessions = (sessions: Session[] = []) => {
  const savedDays = {};
  const datetimes = [];

  for (const { scheduleEvent } of sessions) {
    for (const schedule of scheduleEvent) {
      const [startDate, startHour] = (schedule?.start || "").split(" ");
      const [endDate, endHour] = (schedule?.end || "").split(" ");

      const { value: startDay } = getSelectableDay(startDate);
      const { value: endDay } = getSelectableDay(endDate);

      const dayKey = `${startDay}-${startHour}-${endDay}-${endHour}`;

      if (!(dayKey in savedDays)) {
        savedDays[dayKey] = dayKey;
        const index = datetimes.length + 1;

        datetimes.push(getParsedDatetime(schedule, index));
      }
    }
  }

  return datetimes;
};

export const getSingleEventSessions = (sessions: Session[] = []) => {
  const datetimes = [];

  for (const { scheduleEvent } of sessions) {
    for (const schedule of scheduleEvent) {
      const index = datetimes.length + 1;
      datetimes.push(getParsedDatetime(schedule, index));
    }
  }

  return datetimes;
};

export const getIsCurrentWeek = (week: IntervalWeek) => {
  const todayDate = dayjs().format("YYYY-MM-DD");
  const isCurrentWeek =
    dayjs(todayDate).isSameOrAfter(week?.startingDate, "day") &&
    dayjs(todayDate).isSameOrBefore(week?.endingDate, "day");

  return {
    isCurrentWeek,
    isBeforeCurrentWeek: dayjs(todayDate).isAfter(week?.endingDate, "day"),
  };
};

export const getEditedClassroomAssignedDates = (
  selectedClassrooms: {
    classroom: Classroom;
    dates: any[];
  }[],
) => {
  return selectedClassrooms?.map(({ classroom, dates }) => ({
    classroomId: classroom?.id,
    assignedDates: dates?.map(dateData => {
      const startTime = dateData?.startTime ?? dateData?.start?.value?.value;
      const endTime = dateData?.endTime ?? dateData?.end?.value?.value;
      const date = dateData?.date ?? dateData?.day?.dateValue;

      const [startHour, startMinute, startSecond] = startTime?.split(":");
      const [endHour, endMinute, endSecond] = endTime?.split(":");

      return {
        start: `${date} ${getDateNumber(startHour)}:${getDateNumber(
          startMinute || 0,
        )}:${getDateNumber(startSecond || 0)}`,
        end: `${date} ${getDateNumber(endHour)}:${getDateNumber(endMinute || 0)}:${getDateNumber(
          endSecond || 0,
        )}`,
      };
    }),
  }));
};
